const IFRAME_API_SRC = 'https://www.youtube.com/iframe_api';

export type YoutubeApiLoaderOptions = {
  /** Inject script loader (tests). Default appends a <script> tag once. */
  loadScript?: (src: string) => void;
  /** Window-like host for YT + onYouTubeIframeAPIReady. */
  host?: Window & typeof globalThis;
};

let loadPromise: Promise<void> | null = null;

declare global {
  interface Window {
    YT?: YtNamespace;
    onYouTubeIframeAPIReady?: () => void;
  }
}

/** Minimal typings for the IFrame Player API surface we use. */
export type YtPlayerState = {
  UNSTARTED: number;
  ENDED: number;
  PLAYING: number;
  PAUSED: number;
  BUFFERING: number;
  CUED: number;
};

export type YtPlayer = {
  getCurrentTime(): number;
  getDuration(): number;
  getPlayerState(): number;
  playVideo(): void;
  pauseVideo(): void;
  seekTo(seconds: number, allowSeekAhead: boolean): void;
  destroy(): void;
  getIframe?(): HTMLElement;
  /** Present on real IFrame API players after onReady. */
  getVideoData?(): { title?: string; video_id?: string; author?: string };
  /** Caption module helpers (optional — present on real IFrame API players). */
  loadModule?(moduleName: string): void;
  unloadModule?(moduleName: string): void;
  setOption?(module: string, option: string, value: unknown): void;
  getOption?(module: string, option: string, extras?: unknown): unknown;
  toggleSubtitles?(): void;
};

export type YtPlayerOptions = {
  videoId?: string;
  width?: string | number;
  height?: string | number;
  playerVars?: Record<string, string | number>;
  events?: {
    onReady?: (event: { target: YtPlayer }) => void;
    onStateChange?: (event: { data: number; target: YtPlayer }) => void;
  };
};

export type YtNamespace = {
  Player: new (elementId: string | HTMLElement, options: YtPlayerOptions) => YtPlayer;
  PlayerState: YtPlayerState;
};

function defaultLoadScript(src: string, host: Window & typeof globalThis): void {
  const doc = host.document;
  if (doc.querySelector(`script[src="${src}"]`)) return;
  const script = doc.createElement('script');
  script.src = src;
  script.async = true;
  doc.head.appendChild(script);
}

/** Load the YouTube IFrame API once; resolves when `YT.Player` is available. */
export function loadYoutubeIframeApi(options: YoutubeApiLoaderOptions = {}): Promise<void> {
  const host = options.host ?? window;
  if (host.YT?.Player) return Promise.resolve();

  if (!loadPromise) {
    loadPromise = new Promise<void>((resolve, reject) => {
      let settled = false;
      const finish = (err?: Error) => {
        if (settled) return;
        settled = true;
        if (err) {
          loadPromise = null;
          reject(err);
        } else {
          resolve();
        }
      };

      const previous = host.onYouTubeIframeAPIReady;
      host.onYouTubeIframeAPIReady = () => {
        try {
          previous?.();
        } catch {
          // ignore prior handler errors
        }
        if (host.YT?.Player) finish();
        else finish(new Error('YouTube IFrame API ready without YT.Player'));
      };

      try {
        if (options.loadScript) {
          options.loadScript(IFRAME_API_SRC);
        } else {
          defaultLoadScript(IFRAME_API_SRC, host);
        }
      } catch (err) {
        finish(err instanceof Error ? err : new Error(String(err)));
        return;
      }

      // Race: script may already be on the page and onYouTubeIframeAPIReady already fired
      // before we assigned our callback — poll until YT.Player exists.
      if (host.YT?.Player) {
        finish();
        return;
      }
      const started = Date.now();
      const timer = host.setInterval(() => {
        if (host.YT?.Player) {
          host.clearInterval(timer);
          finish();
        } else if (Date.now() - started > 20_000) {
          host.clearInterval(timer);
          finish(new Error('YouTube IFrame API load timeout'));
        }
      }, 50);
    });
  }

  return loadPromise;
}

/** Test helper: clear the singleton so the next load starts fresh. */
export function resetYoutubeIframeApiLoader(): void {
  loadPromise = null;
}
